import queue from 'async/queue'
import fetch from 'isomorphic-fetch'
import * as Sentry from '@sentry/browser';
import S3 from 'aws-sdk/clients/s3';
import throttle from 'lodash/throttle'

var q = queue(function(task, callback) {
  task.startCb()
  const s3Task = s3Upload(task.file, task.options, task.uploadMetaData, (err, data) => {
    callback(err, data);
  }, task.progressCb, task.logger)
  task.cancel = () => {
    s3Task.cancel()
    callback({'name': 'cancelled'})
  }
}, 3);

export function transcode(options, callback) {
  fetch(`${process.env.REACT_APP_API_HOST}/transcode/${options.shortcode}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options),
    credentials: 'include',
  })
  .then(async (response) => {
    if (response.status == 429) {
      throw Error("You’re uploading too much… please wait a little bit before trying again.")
    }
    if (!response.ok) {
      throw Error(await response.text())
    }
    return response.json()
  })
  .then((json) => {
    if (json) { callback(null, json) }
  })
  .catch(error => {
    Sentry.captureException(error);
    callback(error)
  })
}

function triggerXhr(file, options, uploadMetaData, callback, progressCb) {
  let cancelled = false;
  const formData = new FormData()
  // transcoding starts immediately!
  if (uploadMetaData.transcoder) {
    // add options to POST
    for (const key of Object.keys(options)) {
      formData.append(key, options[key])
    }
  }

  for (const fieldName of Object.keys(uploadMetaData.fields)) {
    formData.append(fieldName, uploadMetaData.fields[fieldName])
  }
  formData.append('file', file)

  const xhr = new window.XMLHttpRequest()
  xhr.open('post', uploadMetaData.url, true)

  xhr.onreadystatechange = () => {
    if (cancelled) {
      return
    }
    if (xhr.readyState === 4) {
      xhr.onerror = null
      if (xhr.status >= 200 && xhr.status < 400) {
        callback(null, xhr.responseText ? JSON.parse(xhr.responseText) : '')
      } else {
        callback(new Error('Cannot connect to server. (' +  (xhr.status || "").toString() + ")"))
      }
    }
  }
  xhr.onerror = (err) => {
    xhr.onreadystatechange = null
    if (cancelled) {
      return;
    }
    let errorThrown = err.message
    if (!errorThrown || errorThrown.length === 0) {
      errorThrown = 'Network error. Please try again.'
    }
    if (errorThrown === 'abort') {
      return
    }

    callback(new Error(errorThrown))
  }
  xhr.send(formData)

  return {
    cancel: () => {
      cancelled = true;
      xhr.abort()
    },
  }
}

export function upload(file, options, uploadMetaData, callback, progressCb, startCb, logger) {
  const task = {
    file,
    options,
    uploadMetaData,
    progressCb,
    startCb,
    logger
  }
  const running = q.running() 
  if (running >= q.concurrency) {
    task.queued = true
  }
  q.push(task, (err, data) => {
    if (err && err.name == 'cancelled') {
      // ignore if it's cancelled
      return
    }
    callback(err, data)
  })
  return task
}

export function s3Upload(file, options, uploadMetaData, callback, progressCb, logger) {
  let aborted = false;
  let xhrTask = null;
  const clockSkew = uploadMetaData.time ? (uploadMetaData.time * 1000) - new Date().getTime() : 0;

  var bucket = new S3({
    apiVersion: '2006-03-01',
    region: 'us-east-1', 
    credentials: uploadMetaData['credentials'],
    useAccelerateEndpoint: uploadMetaData.accelerated,
    logger: {
      log: (message) => {
        if (logger) {
          logger(message)
        }
      }
    },
    maxRetries: 15,
    systemClockOffset: clockSkew,
    retryDelayOptions: {
      customBackoff: (retries, err) => {
        return retries * 1000
      }
    }
  })

  const progressCbThrottled = throttle(progressCb, 1000)
  const upload = bucket.upload({
    Key: uploadMetaData['key'],
    Body: file,
    Bucket: uploadMetaData['bucket'],
    ACL: "public-read"
  }, {
    queueSize: 3
  }, (err, data) => {
    if (err) {
      if (!aborted) {
        logger("S3 Multipart Upload Failed: " + err.name)
        progressCb({
          retries: 1,
          percent: 0,
        })
        xhrTask = triggerXhr(file, options, uploadMetaData, (err, data) => {
          if (err) {
            logger("S3 XHR Upload Failed: " + err.name)
            callback(err);
          } else {
            callback(null, data)
          }
        }, progressCb);
      }
    } else {
      callback(null, data)
    }
  });

  let lastTime = Date.now()
  let lastLoaded = 0
  let lastPercent = 0
  let called = 0
  upload.on('httpUploadProgress', (event) => {
    let speed = 0
    if (event.loaded) {
      const timeDelta = Date.now() - lastTime
      const loadedDelta = event.loaded - lastLoaded

      speed = timeDelta ? (loadedDelta / timeDelta) * 1000 : null

      lastLoaded = event.loaded
      lastTime = Date.now()
    }
    let percent = parseInt((event.loaded / event.total) * 100, 10)
    if (percent < lastPercent) {
      percent = lastPercent
    }
    lastPercent = percent
    if (called < 1000) {
      progressCb({
        percent,
        speed,
      })
    } else {
      progressCbThrottled({
        percent,
        speed,
      })
    }
    called += 1
  });

  return {
    cancel: () => {
      aborted = true;
      upload.abort()
      if (xhrTask) {
        xhrTask.cancel();
      }
    }
  }
}
